import * as React from 'react';

import { Tabs, Tab, Flex, useCompassTheme } from '@circleci/compass';
import { Hit } from 'react-instantsearch-core';
import { Configure, Index, Stats, connectHits } from 'react-instantsearch-dom';

import { Action, LibContext } from '~/contexts/LibContext';
import { styled } from '~/styled';

import {
  DocResultItem,
  OrbResultItem,
  ImgResultItem,
  DocResultHit,
  OrbResultHit,
  ImgResultHit,
} from './SearchResultItem';
import { SearchState } from './SearchState';
import translations from './translations.json';

const DOC_ALGOLIA_INDEX = 'documentation';
const ORB_ALGOLIA_INDEX = 'orbs-prod';
const IMG_ALGOLIA_INDEX = 'images_prod';

export const isBrowser = () => {
  return typeof window !== 'undefined';
};

const StyledTabs = styled(Tabs)(({ theme }) => {
  return `
    padding-top: ${theme.space.space16};
  `;
});

const StyledTab = styled(Tab)(({ theme }) => {
  return `
    text-transform: none;
    font-weight: ${theme.fontWeights.medium};
    font-size: ${theme.fontSizes.fontsize14};
    @media (min-width: ${theme.mediaQueries.large}) {
      font-size: ${theme.fontSizes.fontsize16};
    }
    color: #898989; // not in theme
    padding: ${theme.space.space32} 0;
    background: none;

    &:hover {
      color: #898989; // not in theme
    }

    &[aria-selected='true'],
    &:active,
    &:focus {
      color: ${theme.colors.n700};
      box-shadow: inset 0 -0.25rem ${theme.colors.n700} !important;
    }
  `;
});

const StyledStats = styled.div`
  font-weight: 100; // not in theme
`;

const TabList = styled(Flex)(({ theme }) => {
  return `
    border-bottom: 1px solid #d8d8d8; // not in theme
    background: #fafafa; // not in theme
    position: sticky;
    top: 0px;
    padding-top: ${theme.space.space16};

    @media (max-width: ${theme.mediaQueries.large}) {
      column-gap: ${theme.space.space48};
    }
  `;
});

const HitsContainer = styled.div(`
  padding: 0;
`);

const CustomHits = connectHits(
  ({
    hits,
    index,
  }: {
    hits:
      | Array<Hit<DocResultHit>>
      | Array<Hit<OrbResultHit>>
      | Array<Hit<ImgResultHit>>;
    index?: string;
  }) => {
    return (
      <HitsContainer>
        {hits.map((hit, key) => {
          switch (index) {
            case DOC_ALGOLIA_INDEX:
              return <DocResultItem hit={hit as Hit<DocResultHit>} key={key} />;
            case ORB_ALGOLIA_INDEX:
              return <OrbResultItem hit={hit as Hit<OrbResultHit>} key={key} />;
            case IMG_ALGOLIA_INDEX:
              return <ImgResultItem hit={hit as Hit<ImgResultHit>} key={key} />;
          }
        })}
      </HitsContainer>
    );
  },
);

interface Props {
  defaultTab?: number;
}

export const SearchResults = ({ defaultTab }: Props) => {
  // by default algolia returns default results when query is empty
  // so we are collecting the searchState from the context
  // to not show results in that case
  const { notifyTrackingFired, internalSearchState } =
    React.useContext(LibContext);
  const locale = 'en';

  const theme = useCompassTheme();
  const innerWidth = isBrowser() ? window.innerWidth : 1200;
  const isDesktop =
    innerWidth > parseInt(theme.mediaQueries.large.replace('px', ''));

  const tabs = [
    {
      title: isDesktop
        ? translations[locale].docsTab.long
        : translations[locale].docsTab.short,
      index: DOC_ALGOLIA_INDEX,
      filters: 'collection: cci2',
    },
    {
      title: isDesktop
        ? translations[locale].orbsTab.long
        : translations[locale].orbsTab.short,
      index: ORB_ALGOLIA_INDEX,
    },
    {
      title: isDesktop
        ? translations[locale].imagesTab.long
        : translations[locale].imagesTab.short,
      index: IMG_ALGOLIA_INDEX,
    },
  ];
  const selectedId = defaultTab && defaultTab < tabs.length ? defaultTab : 0;

  const [selectedTab, setSelectedTab] = React.useState(tabs[selectedId].index);

  return (
    <StyledTabs selectedId={selectedTab}>
      <Flex direction={['column']} flex="1">
        <TabList columnGap="space96" wrap>
          {tabs.map((t) => {
            return (
              <StyledTab
                key={t.index}
                id={t.index}
                data-testid={t.index}
                onClick={() => {
                  setSelectedTab(t.index);
                  notifyTrackingFired &&
                    notifyTrackingFired('search-results-tab', Action.clicked, {
                      tab: t.index,
                    });
                }}
              >
                {t.title}{' '}
                <Index indexName={t.index}>
                  <StyledStats>
                    <Stats
                      translations={{
                        stats(nbHits: number) {
                          return `(${internalSearchState.query ? nbHits : 0})`;
                        },
                      }}
                    />
                  </StyledStats>
                </Index>
              </StyledTab>
            );
          })}
        </TabList>

        <Flex flex="auto">
          {tabs.map((t) => {
            return (
              <div key={t.index}>
                <Index indexName={t.index}>
                  {selectedTab === t.index && internalSearchState.query && (
                    <>
                      <CustomHits index={t.index} />
                      <SearchState title={t.title} />
                    </>
                  )}
                  <Configure filters={t.filters ?? ''} hitsPerPage={25} />
                </Index>
              </div>
            );
          })}
        </Flex>
      </Flex>
    </StyledTabs>
  );
};
